import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSET_TYPES = ['Laptop', 'Mobile', 'Tablet', 'Desktop', 'Monitor', 'Keyboard', 'Mouse', 'Printer', 'Furniture', 'Vehicle', 'Other'];
const ASSET_STATUS = ['Available', 'Assigned', 'Under Repair', 'Retired', 'Lost'];

function Assets({ user, onLogout }) {
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  
  const [formData, setFormData] = useState({
    asset_name: '',
    asset_type: 'Laptop',
    serial_number: '',
    photo_url: '',
    purchase_date: '',
    purchase_cost: '',
    warranty_expiry: '',
    status: 'Available',
    condition: 'Good',
    location: '',
    description: '',
    vendor: ''
  });

  useEffect(() => {
    fetchAssets();
    fetchDashboardStats();
    if (user.role === 'Admin' || user.role === 'HR Manager') {
      fetchEmployees();
    }
  }, [filterStatus, filterType]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('asset_type', filterType);

      const response = await axios.get(`${API}/assets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.employee_status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (selectedAsset) {
        await axios.put(`${API}/assets/${selectedAsset.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Asset updated successfully!');
      } else {
        await axios.post(`${API}/assets`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Asset created successfully!');
      }
      resetForm();
      fetchAssets();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to save asset: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setFormData({ ...asset });
    setShowForm(true);
  };

  const handleAssign = async (assetId) => {
    const employeeId = prompt('Enter Employee ID to assign:');
    if (!employeeId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/${assetId}/assign?employee_id=${employeeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Asset assigned successfully!');
      fetchAssets();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to assign asset: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUnassign = async (assetId) => {
    if (!window.confirm('Are you sure you want to unassign this asset?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/${assetId}/unassign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Asset unassigned successfully!');
      fetchAssets();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to unassign asset: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Asset deleted successfully!');
      fetchAssets();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to delete asset: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      asset_name: '',
      asset_type: 'Laptop',
      serial_number: '',
      photo_url: '',
      purchase_date: '',
      purchase_cost: '',
      warranty_expiry: '',
      status: 'Available',
      condition: 'Good',
      location: '',
      description: '',
      vendor: ''
    });
    setSelectedAsset(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    setFormData({
      asset_name: '',
      asset_type: 'Laptop',
      serial_number: '',
      photo_url: '',
      purchase_date: '',
      purchase_cost: '',
      warranty_expiry: '',
      status: 'Available',
      condition: 'Good',
      location: '',
      description: '',
      vendor: ''
    });
    setSelectedAsset(null);
    setShowForm(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Asset Management</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button
              onClick={openAddForm}
              className="px-6 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: '#2b3380'}}
            >
              + Add Asset
            </button>
          )}
        </div>

        {/* Asset Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Asset Management Overview"
            icon="📦"
            bgColor="#00acc1"
            stats={[
              {
                label: 'Total Assets',
                value: dashboardStats.total_assets || 0,
                subtitle: 'All assets',
                color: '#00acc1'
              },
              {
                label: 'Assigned Assets',
                value: dashboardStats.assigned_assets || 0,
                subtitle: 'In use',
                color: '#ff9800'
              },
              {
                label: 'Available Assets',
                value: dashboardStats.available_assets || 0,
                subtitle: 'Ready to assign',
                color: '#4caf50'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Asset holders',
                color: '#2b3380'
              }
            ]}
          />
        )}

        {/* Asset Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedAsset ? 'Edit Asset' : 'Add New Asset'}
                  </h2>
                  <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Asset Name *</label>
                      <input
                        type="text"
                        value={formData.asset_name}
                        onChange={(e) => setFormData({...formData, asset_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Asset Type *</label>
                      <select
                        value={formData.asset_type}
                        onChange={(e) => setFormData({...formData, asset_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      >
                        {ASSET_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Serial Number *</label>
                      <input
                        type="text"
                        value={formData.serial_number}
                        onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Photo URL</label>
                      <input
                        type="text"
                        value={formData.photo_url}
                        onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="https://example.com/photo.jpg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Purchase Date</label>
                      <input
                        type="date"
                        value={formData.purchase_date}
                        onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Purchase Cost (₹)</label>
                      <input
                        type="number"
                        value={formData.purchase_cost}
                        onChange={(e) => setFormData({...formData, purchase_cost: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Warranty Expiry</label>
                      <input
                        type="date"
                        value={formData.warranty_expiry}
                        onChange={(e) => setFormData({...formData, warranty_expiry: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {ASSET_STATUS.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Condition</label>
                      <select
                        value={formData.condition}
                        onChange={(e) => setFormData({...formData, condition: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="Good">Good</option>
                        <option value="Fair">Fair</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Location</label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Vendor</label>
                      <input
                        type="text"
                        value={formData.vendor}
                        onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="3"
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      {selectedAsset ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Status</option>
                {ASSET_STATUS.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Types</option>
                {ASSET_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Assets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assets.map((asset) => (
            <div key={asset.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {asset.photo_url && (
                <img
                  src={asset.photo_url}
                  alt={asset.asset_name}
                  className="w-full h-48 object-cover"
                />
              )}
              {!asset.photo_url && (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                  <span className="text-6xl">📦</span>
                </div>
              )}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold">{asset.asset_name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    asset.status === 'Available' ? 'bg-green-100 text-green-800' :
                    asset.status === 'Assigned' ? 'bg-[#2b338030] text-[#2b3380]' :
                    asset.status === 'Under Repair' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {asset.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Type:</strong> {asset.asset_type}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Serial:</strong> {asset.serial_number}
                </p>
                {asset.assigned_to && (
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Assigned to:</strong> {asset.assigned_to}
                  </p>
                )}
                {asset.purchase_date && (
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Purchase:</strong> {formatDate(asset.purchase_date)}
                  </p>
                )}
                {asset.condition && (
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Condition:</strong> {asset.condition}
                  </p>
                )}

                {(user.role === 'Admin' || user.role === 'HR Manager') && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    <button
                      onClick={() => handleEdit(asset)}
                      className="px-3 py-1 bg-[#2b3380] text-white rounded text-sm hover:bg-[#1f2660]"
                    >
                      Edit
                    </button>
                    {asset.status === 'Available' && (
                      <button
                        onClick={() => handleAssign(asset.id)}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        Assign
                      </button>
                    )}
                    {asset.status === 'Assigned' && (
                      <button
                        onClick={() => handleUnassign(asset.id)}
                        className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                      >
                        Unassign
                      </button>
                    )}
                    {user.role === 'Admin' && (
                      <button
                        onClick={() => handleDelete(asset.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {assets.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-gray-600">No assets found</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Assets;